/**
 * @module app.demo
 */

import { inject } from 'aurelia-framework';
import { IApiQueryResults, IApiStatus, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { httpRequest } from 'app/shared/services/http-service';
import { resolveProperty } from 'app/shared/lib/objects';

export enum SearchMethod {
    StartsWith = 1,
    Contains,
    EndsWith
}

@inject(EnvironmentService)
export class DemoDataService {

    private readonly dataPath: string = '~/app/demo/data/example-data.json';
    private data: any;

    constructor(private readonly env: EnvironmentService, dataPath: string) {
        if(dataPath) { this.dataPath = dataPath; }
        this.load();
    }

    private async load(): Promise<void> {
        if(!this.data) {
            const dataUrl = this.env.resolveUrl(this.dataPath);
            const loadedData = await httpRequest(dataUrl, { format: 'json', cache: 'none' });
            this.data = { ...loadedData };
        }
    }

    ////****************** Functions ************************************************////
    public async loadData(dataSet: string, options?: Partial<IQueryLoadOptions>): Promise<IApiQueryResults<any>> {
        return await this.doLoad(dataSet);
    }

    public async searchData(dataSet: string, searchValue: string, searchProperties: string[], options?: Partial<IQueryLoadOptions>): Promise<IApiQueryResults<any>> {
        if(!dataSet || !searchValue || searchProperties.length === 0) {
            return {
                count: 0,
                results: [],
                resultsType: 'json',
                isSuccessful: false,
                links: null,
                $baseUrl: null,
                status: this.createResultsStatusError("Missing required search fields"),
                timestamp: null
            } as IApiQueryResults<any>;
        }

        const loadResults = await this.doLoad(dataSet);
        if(loadResults.results) {
            return this.doSearch(loadResults.results, searchProperties, searchValue);
        }
        else {
            return {
                count: 0,
                results: [],
                resultsType: 'json',
                isSuccessful: true,
                links: null,
                $baseUrl: null,
                status: null,
                timestamp: null
            } as IApiQueryResults<any>;
        }
    }

    ////****************** Private Functions ************************************************////
    private async doLoad(dataProperty: string): Promise<IApiQueryResults<any>> {
        return new Promise<any>(async (resolve, reject) => {

            if(!dataProperty) {
                reject();
                return;
            }

            await this.load();

            const loadedData = resolveProperty(this.data, dataProperty);

            const results: IApiQueryResults<any> = {
                count: loadedData?.length ?? 0,
                results: loadedData,
                resultsType: 'json',
                isSuccessful: true,
                links: null,
                $baseUrl: null,
                status: null,
                timestamp: null
            };

            resolve(results);

        });
    }

    private async doSearch(data: any[], searchProperties: string[], searchValue: string, method: SearchMethod = SearchMethod.StartsWith): Promise<IApiQueryResults<any>> {
        return new Promise<any>((resolve, reject) => {

            if(!data || searchProperties.length === 0) {
                reject();
                return;
            }

            const searchResults = data.filter((record) => searchProperties.some((property) => {
                const value: string = resolveProperty(record, property)?.toString();
                return value ? value.toLowerCase().startsWith(searchValue.toLowerCase()) : false;
            }));

            const results: IApiQueryResults<any> = {
                count: searchResults.length ?? 0,
                results: searchResults,
                resultsType: 'json',
                isSuccessful: true,
                links: null,
                $baseUrl: null,
                status: null,
                timestamp: null
            };

            resolve(results);

        });
    }

    private createResultsStatusError(description: string, code?: string | null, property?: string | null, appendStatus?: IApiStatus): IApiStatus {
        let status: IApiStatus = {
            warnings: [],
            errors: [{
                code: code ?? '',
                description: description,
                property: property ?? null
            }]
        }
        if(appendStatus) {
            appendStatus.errors.push(status.errors[0]);
            status = appendStatus;
        }
        return status;
    }

}
